import React, { FC, useCallback, useEffect, useState } from 'react';
import styles from './CheckoutDeliverySection.module.css';
import { useNavigate } from 'react-router-dom';

import { DeliveryAddressService } from 'Logic/DeliveryAddressService';
import { useForceLogoutAndRedirect } from 'Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { UnauthorizedPopupStoreImplementation } from 'Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';
import type { IDeliveryCarrier } from '@model/DeliveryAddress.model';
import useImageUploadRules from 'Logic/Functions/Hooks/useImageUploadRules';

const CheckoutDeliveryCarrierSection: FC = () => {
  const service = new DeliveryAddressService();
  const navigate = useNavigate();
  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();

  const { authenticationState } = UserAuthenticationStoreImplementation();
  const authToken = authenticationState?.token?.trim() ?? '';

  const [carriers, setCarriers] = useState<IDeliveryCarrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { selectedDeliveryCarrier, setSelectedDeliveryCarrier } = OrderStoreImplementation();

  const { buildImageSrc: buildIconSrc } = useImageUploadRules({
    mode: 'image',
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  useEffect(() => {
    service.handleGetCarriers({
      token: authToken,
      setLoading,
      setApiError,
      setCarriers,
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate('/', { replace: true });
      },
    });
  }, [authToken]);

  useEffect(() => {
    service.getDefaultCarrier({
      carriers,
      selectedCarrier: selectedDeliveryCarrier,
      setSelectedCarrier: setSelectedDeliveryCarrier,
    });
  }, [carriers, selectedDeliveryCarrier, setSelectedDeliveryCarrier]);

  const handleSelect = useCallback(
    (carrier: IDeliveryCarrier) => {
      setSelectedDeliveryCarrier(carrier);
    },
    [setSelectedDeliveryCarrier]
  );

  return (
    <section className={styles.section_carrier} data-testid="checkout-carriers">
      <h3 className={styles.title_carrier}>Delivery Method</h3>

      {apiError && (
        <div className={styles.error_carrier ?? ''} data-testid="checkout-carriers-error">
          {apiError}
        </div>
      )}

      <div className={styles.grid_carrier} role="radiogroup" aria-label="Delivery carriers">
        {carriers.map((c) => {
          const isSelected = selectedDeliveryCarrier?.id === c.id;

          return (
            <label
              key={c.id}
              className={`${styles.card_carrier} ${isSelected ? styles.selected_carrier : ''}`}
              data-testid={`carrier-card-${c.id}`}
            >
              <input
                type="radio"
                name="deliveryCarrier"
                className={styles.radio_carrier}
                checked={isSelected}
                onChange={() => handleSelect(c)}
                aria-label={`Select ${c.name}`}
              />

              <div className={styles.info_carrier}>
                <div className={styles.row_carrier}>
                  {buildIconSrc(c.icon) && (
                    <img
                      src={buildIconSrc(c.icon)!}
                      alt=""
                      aria-hidden="true"
                      className={styles.icon_carrier}
                    />
                  )}

                  <div className={styles.name_carrier}>{c.name}</div>
                </div>

                <div className={styles.time_carrier}>{c.deliveryTimeEstimation}</div>
              </div>

              <div className={styles.priceBox_carrier}>${Number(c.fees ?? 0).toFixed(2)}</div>
            </label>
          );
        })}
      </div>
    </section>
  );
};

export default CheckoutDeliveryCarrierSection;
